export function Hero() {
  return (
    <section className="relative pt-28 pb-24 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-dark" />
      <div className="absolute inset-0 bg-grid opacity-40" />
      
      {/* Glowing orbs */}
      <div className="absolute top-20 left-1/4 w-96 h-96 bg-brand-600/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-cyan/10 rounded-full blur-3xl" />

      <div className="relative mx-auto max-w-7xl section-padding">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-brand-600/20 border border-brand-500/30 text-brand-400 text-sm font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-accent-emerald mr-2 animate-pulse" />
              Now SOC 2 Type II Certified
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              <span className="text-white">Ship auth in </span>
              <span className="gradient-text">minutes,</span>
              <br />
              <span className="text-white">not months</span>
            </h1>

            <p className="mt-6 text-lg text-surface-400 max-w-xl mx-auto lg:mx-0">
              JumpstoneAuth is a developer-first authentication platform. Add secure login, SSO, MFA, and user management to your app with just a few lines of code.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
              <a href="/signup" className="w-full sm:w-auto btn btn-primary text-base px-8 py-3.5">
                Start Building Free
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="/docs" className="w-full sm:w-auto btn btn-secondary text-base px-8 py-3.5">
                View Documentation
              </a>
            </div>

            {/* Trust indicators */}
            <div className="mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-surface-500">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-accent-emerald" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Free up to 1,000 MAU
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-accent-emerald" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                No credit card required
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-accent-emerald" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                5-minute setup
              </div>
            </div>
          </div>

          {/* Right: Code Example */}
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-brand-600/20 to-accent-cyan/20 rounded-2xl blur-2xl" />
            
            {/* Code block */}
            <div className="relative bg-surface-900 border border-surface-700 rounded-xl overflow-hidden">
              {/* Tab bar */}
              <div className="flex items-center gap-2 px-4 py-3 bg-surface-800/50 border-b border-surface-700">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <span className="ml-2 text-xs text-surface-500">App.tsx</span>
              </div>
              
              {/* Code content */}
              <pre className="p-4 text-sm overflow-x-auto">
                <code className="text-surface-300">
                  <span className="text-brand-400">import</span>{' '}
                  <span className="text-surface-300">{'{'}</span>{' '}
                  <span className="text-accent-cyan">AuthProvider</span>
                  <span className="text-surface-300">{','}</span>{' '}
                  <span className="text-accent-cyan">useAuth</span>{' '}
                  <span className="text-surface-300">{'}'}</span>{' '}
                  <span className="text-brand-400">from</span>{' '}
                  <span className="text-accent-emerald">'@jumpstone/auth-react'</span>
                  <span className="text-surface-500">;</span>
                  {'\n\n'}
                  <span className="text-brand-400">function</span>{' '}
                  <span className="text-accent-amber">App</span>
                  <span className="text-surface-300">() {'{'}</span>
                  {'\n  '}
                  <span className="text-brand-400">return</span>{' '}
                  <span className="text-surface-300">(</span>
                  {'\n    '}
                  <span className="text-surface-500">{'<'}</span>
                  <span className="text-accent-cyan">AuthProvider</span>
                  {'\n      '}
                  <span className="text-surface-400">authUrl</span>
                  <span className="text-surface-500">=</span>
                  <span className="text-accent-emerald">"https://auth.yourapp.com"</span>
                  {'\n    '}
                  <span className="text-surface-500">{'>'}</span>
                  {'\n      '}
                  <span className="text-surface-500">{'<'}</span>
                  <span className="text-accent-cyan">YourApp</span>{' '}
                  <span className="text-surface-500">{'/>'}</span>
                  {'\n    '}
                  <span className="text-surface-500">{'</'}</span>
                  <span className="text-accent-cyan">AuthProvider</span>
                  <span className="text-surface-500">{'>'}</span>
                  {'\n  '}
                  <span className="text-surface-300">);</span>
                  {'\n'}
                  <span className="text-surface-300">{'}'}</span>
                </code>
              </pre>

              {/* Floating notification */}
              <div className="absolute -bottom-4 -right-4 bg-surface-800 border border-surface-700 rounded-lg px-4 py-3 shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-accent-emerald/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-accent-emerald" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">User authenticated</p>
                    <p className="text-xs text-surface-500">MFA verified • 12ms</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted by logos */}
        <div className="mt-20 pt-12 border-t border-surface-800">
          <p className="text-center text-sm text-surface-500 mb-8">Trusted by developers at</p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-50 grayscale">
            <div className="text-2xl font-bold text-surface-400">vercel</div>
            <div className="text-2xl font-bold text-surface-400">Stripe</div>
            <div className="text-2xl font-bold text-surface-400">Linear</div>
            <div className="text-2xl font-bold text-surface-400">Notion</div>
            <div className="text-2xl font-bold text-surface-400">Figma</div>
          </div>
        </div>
      </div>
    </section>
  )
}

